import { IdentityProvider } from '@/client/accounts/types/account-user-management.type';
import { SimpleLockIcon } from '@/utils/helpers';
import classNames from 'classnames';
import { get } from 'lodash';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledIcon = styled.i`
  font-size: var(--xsmall-font-size);
  color: var(--gray-darker);
  cursor: pointer;
`;

const StyledDiv = styled.div`
  width: 100%;

  .p-tooltip-target-wrapper {
    width: 100%;
  }
`;

const StyledDropdown = styled(Dropdown)`
  &.p-disabled {
    opacity: 0.5;
  }
`;

const StyledLockIcon = styled.i`
  margin-right: var(--xsmall-padding);
  position: relative;
  top: var(--xxxsmall-padding);
`;

export type FormikDropdownProps = {
  field: {
    name: string;
    value: object;
    onChange: () => void;
    onBlur: () => void;
  };
  form: { touched: Record<string, any>; errors: Record<string, any> };
  label?: string;
  tooltip?: string;
  id?: string;
  showError?: boolean;
  isSyncedField?: boolean;
  isAdSyncedField?: boolean;
};

export const FormikDropdown: React.FC<FormikDropdownProps & DropdownProps> = ({
  field,
  form,
  label,
  required,
  className,
  tooltip,
  id,
  showError = true,
  isSyncedField,
  isAdSyncedField,
  ...rest
}) => {
  const fieldError = get(form.errors, field.name);
  const fieldTouched = get(form.touched, field.name);
  const tooltipId = id ? `${id}-icon` : undefined;

  const { t } = useTranslation();

  return (
    <>
      <label
        htmlFor={field.name}
        className={classNames({
          'p-error': fieldTouched && !!fieldError,
        })}
      >
        {isSyncedField && (
          <StyledLockIcon>
            <SimpleLockIcon />
          </StyledLockIcon>
        )}
        {label}
        {required ? <span className="red"> *</span> : ''}
        {tooltip && tooltipId && (
          <>
            <Tooltip
              target={`#${tooltipId}`}
              position="top"
              content={tooltip}
              my="center bottom-10"
            />
            <StyledIcon className="pi pi-info-circle ml-1" id={tooltipId} />
          </>
        )}
      </label>
      <StyledDiv>
        <StyledDropdown
          id={id}
          {...field}
          className={classNames(className, {
            'p-invalid': fieldTouched && !!fieldError,
          })}
          value={field.value ?? undefined}
          virtualScrollerOptions={
            rest?.options?.length && rest?.options?.length > 10
              ? { itemSize: 50, autoSize: true }
              : undefined
          }
          {...rest}
          dataKey="id"
          disabled={isSyncedField || rest.disabled}
          tooltip={
            isSyncedField
              ? t('user.identityProvider.syncedField', {
                  identityProvider: isAdSyncedField
                    ? IdentityProvider.MICROSOFT_ENTRA_ID
                    : IdentityProvider.GOOGLE_WORKSPACE,
                })
              : undefined
          }
          tooltipOptions={{
            position: 'right',
            showOnDisabled: true,
          }}
        />
      </StyledDiv>
      {showError && fieldTouched && !!fieldError ? (
        <small className="p-error">{fieldError}</small>
      ) : null}
    </>
  );
};
